import type {
  ApprovalRecord,
  AuditEvent,
  CaseDetail,
  CaseRecord,
  CodeProposalRecord,
  ConnectorRecord,
  DemoActor,
  DemoAnalytics,
  DemoSettings,
  OverviewData,
  PlaybookRecord,
} from "@resolto/domain"
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Cable,
  Check,
  Clock3,
  GitPullRequest,
  Inbox,
  Info,
  LoaderCircle,
  Plus,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
} from "lucide-react"
import Link from "next/link"
import { navigate, useLocation, usePathname } from "next/navigation"
import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import HowItWorksPage from "@/app/(marketing)/how-it-works/page"
import IntegrationsPage from "@/app/(marketing)/integrations/page"
import PricingPage from "@/app/(marketing)/pricing/page"
import ProductPage from "@/app/(marketing)/product/page"
import SecurityPage from "@/app/(marketing)/security/page"
import { AnalyticsCharts } from "@/components/analytics-charts"
import { AppShell } from "@/components/app-shell"
import { ApprovalDecision } from "@/components/approval-decision"
import { CopyButton } from "@/components/copy-button"
import { DataExportButton } from "@/components/data-export-button"
import { EvidenceTimeline } from "@/components/evidence-timeline"
import { NewCaseForm } from "@/components/new-case-form"
import { PageHeader } from "@/components/page-header"
import { SiteFooter } from "@/components/site-footer"
import { SiteHeader } from "@/components/site-header"
import { StatusBadge } from "@/components/status-badge"
import { formatRelativeDate } from "@/lib/format"
import { ApiError, apiRequest } from "./api"
import ResoltoWelcome from "./marketing/texly-welcome"

type WorkspacePayload = {
  actor: DemoActor
  overview: OverviewData
  cases: CaseRecord[]
  approvals: ApprovalRecord[]
  connectors: ConnectorRecord[]
  playbooks: PlaybookRecord[]
  proposals: CodeProposalRecord[]
  analytics: DemoAnalytics
  audit: AuditEvent[]
  settings: DemoSettings
  persistence: {
    provider: "cloudflare-d1"
    workspaceVersion: number
    updatedAt: string
  }
}

type WorkspaceContextValue = {
  data: WorkspacePayload
  refresh: () => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext)
  if (!value) throw new Error("Workspace data is unavailable")
  return value
}

function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <div className="marketing-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <SiteHeader />
      {children}
      <SiteFooter />
    </div>
  )
}

function LoadingScreen({ label = "Loading Resolto…" }: { label?: string }) {
  return (
    <main className="route-state" id="main-content">
      <LoaderCircle className="spin" aria-hidden="true" />
      <h1>{label}</h1>
      <p>Reading the persistent Cloudflare workspace.</p>
    </main>
  )
}

function ErrorScreen({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <main className="route-state" id="main-content">
      <AlertCircle aria-hidden="true" />
      <h1>Resolto could not load this view</h1>
      <p>{error.message}</p>
      {retry ? (
        <button className="button button-primary" type="button" onClick={retry}>
          Try again
        </button>
      ) : (
        <Link className="button button-primary" href="/">
          Return home
        </Link>
      )}
    </main>
  )
}

function SignInPage({ embedded = false }: { embedded?: boolean }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function signIn() {
    setSubmitting(true)
    setError("")
    try {
      await apiRequest<{ authenticated: true }>("/api/session", {
        method: "POST",
        body: JSON.stringify({ workspace: "northstar-demo" }),
      })
      navigate("/app")
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The demo session could not start.")
    } finally {
      setSubmitting(false)
    }
  }

  const content = (
    <main className="auth-page" id="main-content">
      <div className="auth-header">
        <Link className="text-link" href="/">
          <ArrowLeft aria-hidden="true" />
          Resolto
        </Link>
      </div>
      <div className="auth-layout">
        <section className="auth-context">
          <p className="section-kicker">Cloudflare-hosted working demo</p>
          <h1>Follow a support request from report to governed resolution.</h1>
          <p>
            The workspace runs on Cloudflare Pages Functions with persistent D1 state. All customer,
            connector, and provider records remain fictional and visibly labelled.
          </p>
          <ul>
            <li>
              <Check aria-hidden="true" />
              Create and investigate persistent cases
            </li>
            <li>
              <Check aria-hidden="true" />
              Record governed approval decisions
            </li>
            <li>
              <Check aria-hidden="true" />
              Review evidence, playbooks, proposals, analytics, and audit history
            </li>
          </ul>
        </section>
        <section className="auth-panel">
          <span className="auth-panel-icon">
            <ShieldCheck aria-hidden="true" />
          </span>
          <h2>Northstar SaaS demo</h2>
          <p>A secure, same-site demo session is required before the persistent workspace opens.</p>
          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}
          <button
            className="button button-primary button-full button-prominent"
            type="button"
            disabled={submitting}
            onClick={() => void signIn()}
          >
            {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
            {submitting ? "Starting secure session…" : "Continue to demo workspace"}
            {!submitting ? <ArrowRight aria-hidden="true" /> : null}
          </button>
          <p className="auth-security-note">
            No password or external account is required. Do not enter production customer data.
          </p>
        </section>
      </div>
    </main>
  )

  return embedded ? content : <div className="auth-shell">{content}</div>
}

function WorkspaceGate() {
  const [data, setData] = useState<WorkspacePayload | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [requiresSession, setRequiresSession] = useState(false)

  const load = useCallback(async () => {
    try {
      const payload = await apiRequest<WorkspacePayload>("/api/workspace")
      setData(payload)
      setError(null)
      setRequiresSession(false)
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setRequiresSession(true)
        setData(null)
        return
      }
      setError(caught instanceof Error ? caught : new Error("Workspace data is unavailable."))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (requiresSession) return <SignInPage embedded />
  if (error) return <ErrorScreen error={error} retry={() => void load()} />
  if (!data) return <LoadingScreen />

  return (
    <WorkspaceContext.Provider value={{ data, refresh: load }}>
      <AppShell>
        <WorkspaceRoute />
      </AppShell>
    </WorkspaceContext.Provider>
  )
}

function OverviewPage() {
  const { data } = useWorkspace()
  return (
    <div className="page-stack">
      <PageHeader
        title="Overview"
        description="Operational queue, connector health, and measured outcomes for Northstar SaaS."
        meta={
          <span className="page-meta">
            Cloudflare D1 · workspace v{data.persistence.workspaceVersion}
          </span>
        }
        actions={
          <Link className="button button-primary" href="/app/cases/new">
            <Plus aria-hidden="true" />
            Ask Resolto to investigate
          </Link>
        }
      />
      <section aria-labelledby="overview-metrics">
        <h2 id="overview-metrics" className="sr-only">
          Workspace metrics
        </h2>
        <div className="metric-ledger">
          {data.overview.metrics.map((metric) => (
            <Link key={metric.key} href={metric.href} className={`metric-cell tone-${metric.tone}`}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.detail}</small>
              <ArrowRight aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>
      <div className="dashboard-grid">
        <section className="panel dashboard-cases" aria-labelledby="recent-cases-title">
          <div className="panel-heading">
            <div>
              <Inbox aria-hidden="true" />
              <div>
                <h2 id="recent-cases-title">Recent cases</h2>
                <p>Newest persistent activity in the workspace.</p>
              </div>
            </div>
            <Link className="text-link with-arrow" href="/app/cases">
              View all
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
          <CaseTable cases={data.overview.recentCases} compact />
        </section>
        <section className="panel dashboard-queue" aria-labelledby="queue-age-title">
          <div className="panel-heading">
            <div>
              <Clock3 aria-hidden="true" />
              <div>
                <h2 id="queue-age-title">Queue by age</h2>
                <p>Where response attention is needed.</p>
              </div>
            </div>
          </div>
          <div className="queue-list">
            {data.overview.queue.map((item) => (
              <Link href={item.href} key={item.label}>
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </div>
                <span>{item.count}</span>
              </Link>
            ))}
          </div>
        </section>
        <section className="panel dashboard-connectors" aria-labelledby="connector-health-title">
          <div className="panel-heading">
            <div>
              <Cable aria-hidden="true" />
              <div>
                <h2 id="connector-health-title">Connector health</h2>
                <p>Explicit demo adapters and capability boundaries.</p>
              </div>
            </div>
          </div>
          <div className="connector-compact-list">
            {data.connectors.slice(0, 5).map((connector) => (
              <Link href="/app/connectors" key={connector.id}>
                <div className="connector-symbol" aria-hidden="true">
                  {connector.name.slice(0, 1)}
                </div>
                <div>
                  <strong>{connector.name}</strong>
                  <span>Demo adapter</span>
                </div>
                <StatusBadge status={connector.status} />
              </Link>
            ))}
          </div>
        </section>
        <section className="panel dashboard-activity" aria-labelledby="activity-title">
          <div className="panel-heading">
            <div>
              <ShieldCheck aria-hidden="true" />
              <div>
                <h2 id="activity-title">Governed activity</h2>
                <p>Most recent tamper-evident decisions.</p>
              </div>
            </div>
          </div>
          <ol className="activity-list">
            {data.audit.slice(0, 4).map((event) => (
              <li key={event.id}>
                <span className={`activity-dot outcome-${event.outcome}`} aria-hidden="true" />
                <div>
                  <strong>{event.summary}</strong>
                  <span>
                    {event.actor} · {formatRelativeDate(event.timestamp)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  )
}

function CaseTable({ cases, compact = false }: { cases: CaseRecord[]; compact?: boolean }) {
  return (
    <div className="table-scroll">
      <table className={`data-table ${compact ? "dashboard-table" : "cases-table"}`}>
        <thead>
          <tr>
            <th>Case</th>
            <th>Status</th>
            {!compact ? <th>Priority</th> : null}
            {!compact ? <th>Customer</th> : null}
            {!compact ? <th>Assignee</th> : null}
            <th>Confidence</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((caseRecord) => (
            <tr key={caseRecord.id}>
              <td>
                <Link href={`/app/cases/${caseRecord.id}`} className="table-primary-link">
                  <span>{caseRecord.title}</span>
                  <small>
                    {caseRecord.reference} · {caseRecord.customer}
                  </small>
                </Link>
              </td>
              <td>
                <StatusBadge status={caseRecord.status} />
              </td>
              {!compact ? (
                <td>
                  <StatusBadge status={caseRecord.priority} />
                </td>
              ) : null}
              {!compact ? <td>{caseRecord.customer}</td> : null}
              {!compact ? <td>{caseRecord.assignee ?? "Unassigned"}</td> : null}
              <td>
                <StatusBadge status={`${caseRecord.confidence}_confidence`} />
              </td>
              <td className="table-muted">{formatRelativeDate(caseRecord.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CasesPage() {
  const { data } = useWorkspace()
  const location = useLocation()
  const query = new URLSearchParams(location.split("?")[1]?.split("#")[0] ?? "")
  const q = query.get("q")?.toLocaleLowerCase() ?? ""
  const status = query.get("status") ?? ""
  const priority = query.get("priority") ?? ""
  const cases = data.cases.filter(
    (item) =>
      (!q ||
        [item.title, item.reference, item.customer, item.customerReference, item.category]
          .join(" ")
          .toLocaleLowerCase()
          .includes(q)) &&
      (!status || item.status === status) &&
      (!priority || item.priority === priority),
  )
  const csv = [
    ["Reference", "Title", "Status", "Priority", "Customer", "Assignee", "Confidence", "Updated"],
    ...cases.map((item) => [
      item.reference,
      item.title,
      item.status,
      item.priority,
      item.customer,
      item.assignee ?? "",
      item.confidence,
      item.updatedAt,
    ]),
  ]
    .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","))
    .join("\r\n")

  return (
    <div className="page-stack">
      <PageHeader
        title="Cases"
        description="Search, filter, and investigate every support request in the D1-backed workspace."
        meta={
          <span className="page-meta">{cases.length} visible cases · persistent demo data</span>
        }
        actions={
          <>
            <DataExportButton content={csv} fileName="resolto-cases.csv" label="Export CSV" />
            <Link className="button button-primary" href="/app/cases/new">
              <Plus aria-hidden="true" />
              New case
            </Link>
          </>
        }
      />
      <section className="panel cases-panel" aria-label="Case register">
        <form
          className="filter-toolbar"
          onSubmit={(event) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            const next = new URLSearchParams()
            for (const key of ["q", "status", "priority"]) {
              const value = String(form.get(key) ?? "").trim()
              if (value) next.set(key, value)
            }
            navigate(`/app/cases${next.size ? `?${next}` : ""}`)
          }}
        >
          <label className="search-field">
            <Search aria-hidden="true" />
            <span className="sr-only">Search cases</span>
            <input type="search" name="q" defaultValue={query.get("q") ?? ""} />
          </label>
          <select name="status" defaultValue={status} aria-label="Status">
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="needs_context">Needs context</option>
            <option value="investigating">Investigating</option>
            <option value="needs_approval">Needs approval</option>
            <option value="resolved">Resolved</option>
            <option value="escalated">Escalated</option>
          </select>
          <select name="priority" defaultValue={priority} aria-label="Priority">
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <button className="button button-secondary" type="submit">
            Apply filters
          </button>
          {q || status || priority ? (
            <Link className="text-link" href="/app/cases">
              Clear
            </Link>
          ) : null}
        </form>
        {cases.length ? (
          <CaseTable cases={cases} />
        ) : (
          <div className="empty-state">
            <Search aria-hidden="true" />
            <h2>No cases match these filters</h2>
            <p>Clear the filters or create a new investigation.</p>
          </div>
        )}
      </section>
    </div>
  )
}

function NewCasePage() {
  return (
    <div className="page-stack">
      <PageHeader
        title="Ask Resolto to investigate"
        description="Start with plain language. Resolto will preserve the report and ask only for missing context."
      />
      <section className="panel intake-panel">
        <NewCaseForm />
      </section>
    </div>
  )
}

function CasePage({ caseId }: { caseId: string }) {
  const { data, refresh } = useWorkspace()
  const [caseRecord, setCaseRecord] = useState<CaseDetail | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [investigating, setInvestigating] = useState(false)

  const load = useCallback(async () => {
    try {
      setCaseRecord(await apiRequest<CaseDetail>(`/api/cases/${encodeURIComponent(caseId)}`))
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error("Case not found."))
    }
  }, [caseId])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <ErrorScreen error={error} />
  if (!caseRecord) return <LoadingScreen label="Loading case…" />

  const approval = data.approvals.find((item) => item.caseId === caseRecord.id)
  const canInvestigate = ["new", "ready", "needs_context", "failed"].includes(caseRecord.status)

  async function investigate() {
    setInvestigating(true)
    try {
      await apiRequest(`/api/cases/${encodeURIComponent(caseId)}/investigate`, {
        method: "POST",
        idempotent: true,
      })
      await Promise.all([load(), refresh()])
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error("Investigation could not start."))
    } finally {
      setInvestigating(false)
    }
  }

  return (
    <div className="case-workspace">
      <header className="case-workspace-header">
        <div className="case-breadcrumb">
          <Link href="/app/cases">
            <ArrowLeft aria-hidden="true" />
            Cases
          </Link>
          <span>{caseRecord.reference}</span>
        </div>
        <div className="case-heading-row">
          <div>
            <div className="case-heading-meta">
              <StatusBadge status={caseRecord.status} />
              <StatusBadge status={caseRecord.priority} />
              <span>{caseRecord.source === "slack" ? "Slack" : "Web workspace"}</span>
            </div>
            <h1>{caseRecord.title}</h1>
            <p>
              {caseRecord.customer} · opened {formatRelativeDate(caseRecord.createdAt)}
            </p>
          </div>
          {canInvestigate ? (
            <button
              className="button button-primary"
              type="button"
              disabled={investigating}
              onClick={() => void investigate()}
            >
              {investigating ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
              {investigating ? "Starting…" : "Start bounded investigation"}
            </button>
          ) : null}
        </div>
      </header>
      <div className="case-regions">
        <aside className="case-left-rail" aria-label="Nearby cases">
          <div className="rail-heading">
            <strong>Current queue</strong>
            <span>{data.cases.length} cases</span>
          </div>
          <nav>
            {data.cases.slice(0, 8).map((item) => (
              <Link
                href={`/app/cases/${item.id}`}
                key={item.id}
                aria-current={item.id === caseRecord.id ? "page" : undefined}
              >
                <span>{item.reference}</span>
                <strong>{item.title}</strong>
                <small>{item.customer}</small>
                <StatusBadge status={item.status} />
              </Link>
            ))}
          </nav>
        </aside>
        <section className="case-centre" aria-labelledby="investigation-title">
          {caseRecord.clarification.missing.length ? (
            <div className="clarification-banner">
              <div>
                <strong>One answer is still needed</strong>
                <p>{caseRecord.clarification.missing[0]}</p>
              </div>
              <span className="page-meta">Waiting for requester</span>
            </div>
          ) : null}
          <div className="case-section-heading">
            <div>
              <h2 id="investigation-title">Investigation timeline</h2>
              <p>Facts, decisions, and system state in chronological order.</p>
            </div>
            <span>{caseRecord.timeline.length} events</span>
          </div>
          <EvidenceTimeline events={caseRecord.timeline} />
        </section>
        <aside className="case-right-rail" aria-label="Case summary and evidence">
          <section>
            <div className="side-section-heading">
              <h2>Likely cause</h2>
              {caseRecord.finding ? (
                <StatusBadge status={`${caseRecord.finding.confidence}_confidence`} />
              ) : null}
            </div>
            {caseRecord.finding ? (
              <>
                <strong className="finding-title">{caseRecord.finding.title}</strong>
                <p>{caseRecord.finding.rationale}</p>
                <div className="finding-next-step">
                  <span>Recommended next step</span>
                  <p>{caseRecord.finding.nextStep}</p>
                </div>
              </>
            ) : (
              <div className="inline-empty-state">
                <p>No diagnosis yet. Evidence collection is incomplete or still running.</p>
              </div>
            )}
          </section>
          {approval ? (
            <section className="approval-summary">
              <h2>Approval</h2>
              <StatusBadge status={approval.status} />
              <strong>{approval.action}</strong>
              <p>{approval.expectedEffect}</p>
              <Link className="button button-primary button-full" href="/app/approvals">
                Review exact change
              </Link>
            </section>
          ) : null}
          <section>
            <div className="side-section-heading">
              <h2>Affected record</h2>
              <CopyButton value={caseRecord.affectedEntity.identifier} label="Copy identifier" />
            </div>
            <strong>{caseRecord.affectedEntity.label}</strong>
            <code>{caseRecord.affectedEntity.identifier}</code>
          </section>
          <section>
            <div className="side-section-heading">
              <h2>Evidence</h2>
              <span>{caseRecord.evidence.length}</span>
            </div>
            <div className="evidence-link-list">
              {caseRecord.evidence.map((evidence) => (
                <a href={`#timeline-${evidence.id}`} key={evidence.id}>
                  <span>
                    <strong>{evidence.source}</strong>
                    <small>
                      {evidence.recordType} · {evidence.trust}
                    </small>
                  </span>
                  <Check aria-hidden="true" />
                </a>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

function ApprovalsPage() {
  const { data } = useWorkspace()
  const waiting = data.approvals.filter((item) => item.status === "waiting")
  const completed = data.approvals.filter((item) => item.status !== "waiting")
  return (
    <div className="page-stack approvals-page">
      <PageHeader
        title="Approvals"
        description="Review exact scope, evidence, authority, and verified effect before recording a decision."
        meta={<span className="page-meta">{waiting.length} waiting</span>}
      />
      <section className="approval-list">
        <div className="section-heading-inline">
          <div>
            <h2>Waiting for me</h2>
            <p>Nothing executes optimistically when you decide.</p>
          </div>
        </div>
        {waiting.map((approval) => (
          <ApprovalDecision key={approval.id} initialApproval={approval} />
        ))}
      </section>
      {completed.length ? (
        <section className="approval-list">
          <div className="section-heading-inline">
            <div>
              <h2>Completed decisions</h2>
              <p>Persistent decision history remains attached to the audit stream.</p>
            </div>
          </div>
          {completed.map((approval) => (
            <ApprovalDecision key={approval.id} initialApproval={approval} />
          ))}
        </section>
      ) : null}
    </div>
  )
}

function PlaybooksPage() {
  const { data } = useWorkspace()
  return (
    <div className="page-stack">
      <PageHeader
        title="Playbooks"
        description="Versioned, testable diagnostic paths confirmed by developers and technical operators."
        meta={<span className="page-meta">{data.playbooks.length} persistent playbooks</span>}
        actions={
          <Link className="button button-primary" href="/app/playbooks/new">
            <Plus aria-hidden="true" />
            New playbook
          </Link>
        }
      />
      <section className="panel">
        <div className="table-scroll">
          <table className="data-table playbook-table">
            <thead>
              <tr>
                <th>Playbook</th>
                <th>State</th>
                <th>Reliability</th>
                <th>Runs</th>
                <th>Tests</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {data.playbooks.map((playbook) => (
                <tr key={playbook.id}>
                  <td>
                    <span className="table-primary-link">
                      <span>{playbook.name}</span>
                      <small>
                        {playbook.category} · v{playbook.version}
                      </small>
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={playbook.state} />
                  </td>
                  <td>
                    {playbook.reliability
                      ? `${Math.round(playbook.reliability * 100)}%`
                      : "Not measured"}
                  </td>
                  <td>{playbook.runs}</td>
                  <td>{playbook.testStatus.replaceAll("_", " ")}</td>
                  <td>{playbook.owner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function PlaybookCreator() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError("")
    const form = new FormData(event.currentTarget)
    try {
      await apiRequest("/api/playbooks", {
        method: "POST",
        idempotent: true,
        body: JSON.stringify({
          name: form.get("name"),
          category: form.get("category"),
          steps: String(form.get("steps") ?? "")
            .split("\n")
            .map((step) => step.trim())
            .filter(Boolean),
        }),
      })
      navigate("/app/playbooks")
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The playbook could not be saved.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Create playbook"
        description="Create a persistent draft from an explicit, reviewable sequence of diagnostic steps."
      />
      <form className="panel intake-form" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <label>
          Playbook name
          <input name="name" required minLength={4} maxLength={120} />
        </label>
        <label>
          Category
          <input name="category" required minLength={3} maxLength={80} />
        </label>
        <label>
          Diagnostic steps
          <textarea
            name="steps"
            required
            minLength={10}
            maxLength={2_000}
            placeholder={
              "Identify the affected customer\nRead the approved record\nValidate required fields\nEscalate or request approval"
            }
          />
          <small>One allowlisted step per line. Drafts never execute automatically.</small>
        </label>
        <div className="form-actions">
          <Link className="button button-secondary" href="/app/playbooks">
            Cancel
          </Link>
          <button className="button button-primary" type="submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save />}
            {submitting ? "Saving…" : "Save persistent draft"}
          </button>
        </div>
      </form>
    </div>
  )
}

function ConnectorsPage() {
  const { data } = useWorkspace()
  return (
    <div className="page-stack">
      <PageHeader
        title="Connectors"
        description="Inspect capability boundaries, data classes, health, and permitted operations."
        meta={<span className="page-meta">Fictional adapters · no external credentials</span>}
      />
      <div className="connector-grid">
        {data.connectors.map((connector) => (
          <ConnectorCard connector={connector} key={connector.id} />
        ))}
      </div>
      <section className="panel connector-boundary-note">
        <ShieldCheck aria-hidden="true" />
        <div>
          <h2>Truthful demo boundary</h2>
          <p>
            These adapters return fictional records through typed capability contracts. They do not
            claim Slack, Stripe, Sentry, GitHub, Linear, Jira, or customer databases are connected.
          </p>
        </div>
      </section>
    </div>
  )
}

function ConnectorCard({ connector }: { connector: ConnectorRecord }) {
  const [checking, setChecking] = useState(false)
  const [checked, setChecked] = useState(false)
  return (
    <article className="connector-card">
      <header>
        <div className="connector-logo" aria-hidden="true">
          {connector.kind.slice(0, 2)}
        </div>
        <div>
          <h2>{connector.name}</h2>
          <p>{connector.kind} · demo adapter</p>
        </div>
      </header>
      <div className="connector-health-row">
        <StatusBadge status={connector.status} />
        <span>{checked ? "Contract fixture responded now" : "No external network request"}</span>
      </div>
      <dl>
        <div>
          <dt>Risk boundary</dt>
          <dd>{connector.risk.replaceAll("_", " ")}</dd>
        </div>
        <div>
          <dt>Data accessed</dt>
          <dd>{connector.dataClasses.join(", ")}</dd>
        </div>
      </dl>
      <div className="connector-detail-list">
        <span>Allowed operations</span>
        <ul>
          {connector.allowedOperations.map((operation) => (
            <li key={operation}>{operation}</li>
          ))}
        </ul>
      </div>
      <button
        className="button button-secondary"
        type="button"
        disabled={checking || connector.status === "disconnected"}
        onClick={() => {
          setChecking(true)
          window.setTimeout(() => {
            setChecking(false)
            setChecked(true)
          }, 500)
        }}
      >
        {checking ? <LoaderCircle className="spin" aria-hidden="true" /> : <Cable />}
        {checking ? "Testing fixture…" : "Test demo contract"}
      </button>
    </article>
  )
}

function ProposalsPage() {
  const { data } = useWorkspace()
  return (
    <div className="page-stack">
      <PageHeader
        title="Code proposals"
        description="Bounded changes prepared for normal developer review—never autonomous merge or deployment."
        meta={<span className="page-meta">{data.proposals.length} demo proposals</span>}
      />
      <section className="proposal-boundary panel">
        <ShieldCheck aria-hidden="true" />
        <div>
          <h2>Developer authority is unchanged</h2>
          <p>
            Review decisions persist in D1. No repository, merge, or deployment permission exists.
          </p>
        </div>
      </section>
      <div className="proposal-list">
        {data.proposals.map((proposal) => (
          <Link href={`/app/code-proposals/${proposal.id}`} key={proposal.id}>
            <div className="proposal-symbol">
              <GitPullRequest aria-hidden="true" />
            </div>
            <div>
              <span>{proposal.repository}</span>
              <h2>{proposal.title}</h2>
              <p>
                {proposal.files.length} files · {proposal.tests.length} tests · simulated proposal
              </p>
            </div>
            <StatusBadge status={proposal.status} />
            <ArrowRight aria-hidden="true" />
          </Link>
        ))}
      </div>
    </div>
  )
}

function ProposalPage({ proposalId }: { proposalId: string }) {
  const { data } = useWorkspace()
  const proposal = data.proposals.find((item) => item.id === proposalId)
  const [decision, setDecision] = useState(proposal?.status ?? "ready_for_review")
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  if (!proposal) return <ErrorScreen error={new Error("Code proposal not found.")} />

  async function review(next: "accepted" | "changes_requested" | "rejected") {
    setSubmitting(true)
    setError("")
    try {
      const updated = await apiRequest<CodeProposalRecord>(
        `/api/code-proposals/${encodeURIComponent(proposalId)}/review`,
        {
          method: "POST",
          idempotent: true,
          body: JSON.stringify({
            decision: next,
            reason: reason || "Reviewed in the demo workspace.",
          }),
        },
      )
      setDecision(updated.status)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The review was not recorded.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title={proposal.title}
        description={`${proposal.repository} · bounded demo proposal`}
        meta={<StatusBadge status={decision} />}
      />
      <section className="panel">
        <h2>Changed files</h2>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Path</th>
                <th>Additions</th>
                <th>Deletions</th>
              </tr>
            </thead>
            <tbody>
              {proposal.files.map((file) => (
                <tr key={file.path}>
                  <td>
                    <code>{file.path}</code>
                  </td>
                  <td>+{file.additions}</td>
                  <td>−{file.deletions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel proposal-review-form">
        <h2>Developer review</h2>
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <label>
          Review note
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <div className="button-row">
          <button
            className="button button-primary"
            type="button"
            disabled={submitting}
            onClick={() => void review("accepted")}
          >
            Accept for normal review
          </button>
          <button
            className="button button-secondary"
            type="button"
            disabled={submitting || reason.trim().length < 3}
            onClick={() => void review("changes_requested")}
          >
            Request changes
          </button>
          <button
            className="button button-danger-ghost"
            type="button"
            disabled={submitting || reason.trim().length < 3}
            onClick={() => void review("rejected")}
          >
            Reject
          </button>
        </div>
        <p>No merge, pull request, or deployment occurs.</p>
      </section>
    </div>
  )
}

function AnalyticsPage() {
  const { data } = useWorkspace()
  const report = JSON.stringify(
    { generatedFrom: "labelled persistent Resolto demo data", ...data.analytics },
    null,
    2,
  )
  return (
    <div className="page-stack">
      <PageHeader
        title="Analytics and ROI"
        description="Measured outcomes and clearly labelled estimates for the current reporting period."
        meta={<span className="page-meta">{data.analytics.period}</span>}
        actions={
          <DataExportButton
            content={report}
            fileName="resolto-analytics.json"
            label="Export report"
            mediaType="application/json"
          />
        }
      />
      <section className="analytics-metrics" aria-label="Key outcomes">
        {data.analytics.metrics.map((metric) => (
          <Link href={metric.href} key={metric.label}>
            <span>
              {metric.label}
              {metric.estimated ? <em>Estimate</em> : null}
            </span>
            <strong>{metric.value}</strong>
            <small>{metric.change}</small>
            <ArrowRight aria-hidden="true" />
          </Link>
        ))}
      </section>
      <AnalyticsCharts analytics={data.analytics} />
      <section className="panel methodology-panel" id="methodology">
        <Info aria-hidden="true" />
        <div>
          <h2>Estimate methodology</h2>
          <p>{data.analytics.methodology}</p>
          <strong>No financial or performance claim is implied by demo data.</strong>
        </div>
      </section>
    </div>
  )
}

function AuditPage() {
  const { data } = useWorkspace()
  const exportPayload = JSON.stringify(data.audit, null, 2)
  return (
    <div className="page-stack">
      <PageHeader
        title="Audit"
        description="Append-only, organisation-scoped records with a verifiable hash chain."
        meta={<span className="page-meta">{data.audit.length} persistent events</span>}
        actions={
          <DataExportButton
            content={exportPayload}
            fileName="resolto-audit.json"
            label="Export JSON"
            mediaType="application/json"
          />
        }
      />
      <section className="panel">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Outcome</th>
                <th>Hash</th>
              </tr>
            </thead>
            <tbody>
              {data.audit.map((event) => (
                <tr key={event.id}>
                  <td>{formatRelativeDate(event.timestamp)}</td>
                  <td>{event.actor}</td>
                  <td>
                    <code>{event.action}</code>
                  </td>
                  <td>{event.target}</td>
                  <td>
                    <StatusBadge status={event.outcome} />
                  </td>
                  <td>
                    <CopyButton value={event.hash} label="Copy audit hash" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function SettingsPage() {
  const { data, refresh } = useWorkspace()
  const [settings, setSettings] = useState(data.settings)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState("")

  async function save() {
    setSaving(true)
    setError("")
    try {
      const updated = await apiRequest<DemoSettings>("/api/settings", {
        method: "PUT",
        idempotent: true,
        body: JSON.stringify(settings),
      })
      setSettings(updated)
      setSaved(true)
      await refresh()
      window.setTimeout(() => setSaved(false), 2_000)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Settings were not saved.")
    } finally {
      setSaving(false)
    }
  }

  async function resetDemo() {
    if (!window.confirm("Reset all persistent Resolto demo changes in Cloudflare D1?")) return
    await apiRequest("/api/demo/reset", { method: "POST", idempotent: true })
    await refresh()
    navigate("/app")
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Settings"
        description="Persistent workspace identity, support boundary, approval policy, and retention."
      />
      <div className="settings-sections">
        <section className="panel">
          <div>
            <h2>Workspace</h2>
            <p>Stored in the tenant-scoped D1 state document.</p>
          </div>
          <div className="settings-fields">
            <label>
              Workspace name
              <input
                value={settings.workspaceName}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, workspaceName: event.target.value }))
                }
              />
            </label>
            <label>
              Timezone
              <select
                value={settings.timezone}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, timezone: event.target.value }))
                }
              >
                <option>UTC</option>
                <option>Africa/Tripoli</option>
                <option>America/New_York</option>
              </select>
            </label>
            <label>
              Support boundary
              <textarea
                value={settings.supportBoundary}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, supportBoundary: event.target.value }))
                }
              />
            </label>
          </div>
        </section>
        <section className="panel">
          <div>
            <h2>Risk and retention</h2>
            <p>High-risk actions remain disabled by product policy.</p>
          </div>
          <div className="settings-fields">
            <label>
              Evidence retention
              <select
                value={String(settings.evidenceRetentionDays)}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    evidenceRetentionDays: Number(event.target.value) as 30 | 90 | 180 | 365,
                  }))
                }
              >
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">365 days</option>
              </select>
            </label>
            <label className="toggle-row">
              <span>
                <strong>Require recent authentication</strong>
                <small>For every moderate-risk approval</small>
              </span>
              <input
                type="checkbox"
                checked={settings.requireRecentAuthentication}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    requireRecentAuthentication: event.target.checked,
                  }))
                }
              />
            </label>
            <label className="toggle-row">
              <span>
                <strong>Mask PII in evidence previews</strong>
                <small>Full values remain permission-gated</small>
              </span>
              <input
                type="checkbox"
                checked={settings.maskPiiInPreviews}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    maskPiiInPreviews: event.target.checked,
                  }))
                }
              />
            </label>
          </div>
        </section>
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="settings-save-bar">
          <span role="status" aria-live="polite">
            {saved ? (
              <>
                <Check aria-hidden="true" />
                Saved to Cloudflare D1
              </>
            ) : (
              "Policy changes require an explicit save."
            )}
          </span>
          <div className="button-row">
            <button className="button button-danger-ghost" type="button" onClick={resetDemo}>
              <RotateCcw aria-hidden="true" />
              Reset persistent demo
            </button>
            <button
              className="button button-primary"
              type="button"
              disabled={saving}
              onClick={save}
            >
              {saving ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save />}
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function WorkspaceRoute() {
  const pathname = usePathname()
  if (pathname === "/app") return <OverviewPage />
  if (pathname === "/app/cases") return <CasesPage />
  if (pathname === "/app/cases/new") return <NewCasePage />
  const caseMatch = pathname.match(/^\/app\/cases\/([^/]+)$/)
  if (caseMatch?.[1]) return <CasePage caseId={decodeURIComponent(caseMatch[1])} />
  if (pathname === "/app/approvals") return <ApprovalsPage />
  if (pathname === "/app/playbooks") return <PlaybooksPage />
  if (pathname === "/app/playbooks/new") return <PlaybookCreator />
  if (pathname === "/app/connectors") return <ConnectorsPage />
  if (pathname === "/app/code-proposals") return <ProposalsPage />
  const proposalMatch = pathname.match(/^\/app\/code-proposals\/([^/]+)$/)
  if (proposalMatch?.[1]) return <ProposalPage proposalId={decodeURIComponent(proposalMatch[1])} />
  if (pathname === "/app/analytics") return <AnalyticsPage />
  if (pathname === "/app/audit") return <AuditPage />
  if (pathname === "/app/settings") return <SettingsPage />
  return <ErrorScreen error={new Error("Workspace page not found.")} />
}

function ContactPage() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError("")
    const form = new FormData(event.currentTarget)
    try {
      await apiRequest("/api/contact", {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form.entries())),
      })
      setSubmitted(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The request could not be submitted.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">Design-partner access</p>
          <h1>Start with one recurring support interruption.</h1>
          <p>
            Tell us about the request volume, current stack, and developer interruption you want to
            reduce. Submissions are stored in the project’s D1 database.
          </p>
        </div>
      </section>
      <section className="marketing-section">
        <div className="marketing-container contact-layout">
          <div className="contact-context">
            <h2>A focused first scope</h2>
            <ul>
              <li>20–100 employee B2B SaaS team</li>
              <li>Slack-based operational support</li>
              <li>One application, database, and error source</li>
              <li>Five repeatable diagnostic playbooks</li>
            </ul>
          </div>
          {submitted ? (
            <div className="panel intake-result" role="status">
              <Check aria-hidden="true" />
              <h2>Request received</h2>
              <p>Your design-partner request is now recorded in Cloudflare D1.</p>
            </div>
          ) : (
            <form className="panel intake-form" onSubmit={(event) => void submit(event)}>
              {error ? (
                <div className="form-error" role="alert">
                  {error}
                </div>
              ) : null}
              <label>
                Name
                <input name="name" autoComplete="name" required minLength={2} maxLength={100} />
              </label>
              <label>
                Work email
                <input name="email" type="email" autoComplete="email" required maxLength={254} />
              </label>
              <label>
                Company
                <input name="company" autoComplete="organization" required maxLength={120} />
              </label>
              <label>
                Monthly request volume
                <select name="volume" required defaultValue="">
                  <option value="" disabled>
                    Select a range
                  </option>
                  <option value="under-50">Under 50</option>
                  <option value="50-150">50–150</option>
                  <option value="151-500">151–500</option>
                  <option value="500-plus">500+</option>
                </select>
              </label>
              <label>
                Recurring interruption
                <textarea name="message" required minLength={20} maxLength={2_000} />
              </label>
              <button className="button button-primary" type="submit" disabled={submitting}>
                {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
                {submitting ? "Submitting…" : "Submit design-partner request"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  )
}

function LegalPage({ kind }: { kind: "privacy" | "terms" }) {
  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">Legal placeholder</p>
          <h1>{kind === "privacy" ? "Privacy notice" : "Terms of service"}</h1>
          <p>
            This working product is a labelled demonstration. Qualified legal review is required
            before accepting production customer data or commercial access.
          </p>
        </div>
      </section>
      <section className="marketing-section">
        <div className="marketing-container legal-document">
          <h2>Current demo boundary</h2>
          <p>
            Do not submit secrets or production customer data. Demo workspace changes and contact
            submissions are stored in Cloudflare D1 for functional evaluation.
          </p>
          <h2>Data handling</h2>
          <p>
            The application uses same-site session cookies, request validation, rate limits, and
            tenant-scoped state. External provider integrations are not enabled.
          </p>
        </div>
      </section>
    </main>
  )
}

function NotFoundPage() {
  return (
    <MarketingShell>
      <main className="route-state" id="main-content">
        <h1>Page not found</h1>
        <p>The requested Resolto page does not exist.</p>
        <Link className="button button-primary" href="/">
          Return home
        </Link>
      </main>
    </MarketingShell>
  )
}

function updateDocumentTitle(pathname: string) {
  const name =
    pathname === "/"
      ? "Resolve more. Interrupt developers less."
      : pathname
          .split("/")
          .filter(Boolean)
          .at(-1)
          ?.replaceAll("-", " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
  document.title = `${name ?? "Resolto"} · Resolto`
}

export function App() {
  const location = useLocation()
  const pathname = location.split(/[?#]/, 1)[0] ?? "/"
  useEffect(() => updateDocumentTitle(pathname), [pathname])

  if (pathname.startsWith("/app")) return <WorkspaceGate key={location} />
  if (pathname === "/sign-in") return <SignInPage />
  if (pathname === "/") return <ResoltoWelcome />

  const marketingPage = (() => {
    if (pathname === "/product") return <ProductPage />
    if (pathname === "/how-it-works") return <HowItWorksPage />
    if (pathname === "/integrations") return <IntegrationsPage />
    if (pathname === "/security") return <SecurityPage />
    if (pathname === "/pricing") return <PricingPage />
    if (pathname === "/contact") return <ContactPage />
    if (pathname === "/legal/privacy") return <LegalPage kind="privacy" />
    if (pathname === "/legal/terms") return <LegalPage kind="terms" />
    return null
  })()

  return marketingPage ? <MarketingShell>{marketingPage}</MarketingShell> : <NotFoundPage />
}
